import { forwardRef, useEffect, useState } from "react";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { common } from "includes";
import { fetchOptions, peekOptions } from "./optionCache";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ✅ Remove Button that stops drag behavior
const CustomMultiValueRemove = (props: any) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering drag
    if (props.innerProps?.onClick) props.innerProps.onClick(e);
  };

  return (
    <div
      {...props.innerProps}
      onClick={handleClick}
      style={{ padding: '0 8px', cursor: 'pointer' }}
    >
      ×
    </div>
  );
};

// ✅ Draggable MultiValue (whole chip is draggable, except "×")
const DraggableMultiValue = (props: any) => {
  const { data } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: data.value });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? "grabbing" : "grab",
    // The chip being carried rides above its neighbours instead of being
    // overlapped by whichever happens to paint later.
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.85 : undefined,
    // Without this the browser claims the gesture for scrolling and the drag
    // dies halfway on touch.
    touchAction: "none" as const,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
    >
      <components.MultiValue {...props} />
    </div>
  );
};

const MultiFieldSelect = forwardRef((props: any, ref) => {
  const { render, placeholder, clearable, finder, size, auth, locality } = props;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedValue, setSelectedValue] = useState<any[]>(props.value || []);

  // The pointer sensor previously had no activation constraint, so a drag began
  // the instant a chip was pressed. That left no gap between "click" and
  // "drag" — and because dragging starts on pointer-down while the × remove
  // button only guards the later click event, pressing × started a drag the
  // guard could never cancel. An 8px threshold restores the distinction and
  // fixes the × as a side effect.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    // Nothing could reorder units without a mouse until now.
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (render === "empty") {
      setData([]);
      return;
    }
    if (render === "no") return;

    let live = true;
    const authKey = auth || "token";
    const cached = peekOptions(props.api, props.query, authKey);

    if (!cached) {
      setData([]);
      setLoading(true);
    }

    fetchOptions(props.api, props.query, authKey).then((rows: any) => {
      if (!live) return;
      const selectData = common.SetMultiSelect(
        rows,
        props.get,
        props.set,
        props.join
      );
      setData(selectData);

      if (finder || props.value) {
        const selected =
          typeof props.value === "object"
            ? common.FindMultiSelect1(selectData, props.value)
            : common.FindSelect(selectData, props?.value);
        props?.onSelect(selected);
        setSelectedValue(selected);
      }
      setLoading(false);
    });

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render, locality, props.api, props.query]);

  useEffect(() => {
    // setSelectedValue(props.value || []);
    const parsedValue = Array.isArray(props.value)
      ? props.value.map((v: any) => {
        if (typeof v === "string") return { label: v, value: v };
        if (v?.value && v?.label) return v;
        if (v?.unitName) return { label: v.unitName, value: v.unitName };
        return null;
      }).filter(Boolean)
      : [];
    setSelectedValue(parsedValue);
  }, [props.value]);

  //   useEffect(() => {
  //   const safeInitialValue = (props.value || []).map((v: any, index: number) => ({
  //     ...v,
  //     value: v?.value ?? `${props?.join || "initial"}-${index}-${Date.now()}`,
  //     label: typeof v.label === "string" ? v.label.trim() : v.label ?? `Option ${index + 1}`,
  //   }));

  //   setSelectedValue(safeInitialValue);
  // }, [props.value]);

  const onChange = (value: any) => {
    // const cleanValue = (value || []).map((v: any, index: number) => {
    //   const safeValue = v?.value ?? `${props?.join || "select"}-${index}-${Date.now()}`;
    //   return {
    //     ...v,
    //     value: safeValue,
    //     label: typeof v.label === "string" ? v.label.trim() : v.label ?? `Option ${index + 1}`,
    //   };
    // });
    // const deduped = Array.from(new Map(cleanValue.map((item: any) => [item.value, item])).values());

    // setSelectedValue(deduped || []);
    // props.onSelect(deduped);
    setSelectedValue(value || []);
    props.onSelect(value);
  };

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedValue.findIndex((i) => i.value === active.id);
    const newIndex = selectedValue.findIndex((i) => i.value === over.id);

    const reordered = [...selectedValue];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    setSelectedValue(reordered);
    props.onSelect(reordered);
  };

  const SelectComponent = props?.creatable ? CreatableSelect : Select;

  //   const itemKeys = selectedValue.map((item) => item.value);
  // const uniqueKeys = new Set(itemKeys);
  // if (itemKeys.length !== uniqueKeys.size) {
  //   console.log("Duplicate keys detected:", itemKeys);
  // }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={(selectedValue || [])
          .map((item) => item?.value)
          .filter((v, i, arr) => v && arr.indexOf(v) === i)}
        // Unit chips flow horizontally and wrap onto further lines. The
        // vertical strategy used here before computed every movement as though
        // they were stacked in a single column, which is what made the reorder
        // feel like it was fighting the pointer. rectSortingStrategy measures
        // the actual rectangles, so it handles the wrap.
        strategy={rectSortingStrategy}
      >
        <SelectComponent
          ref={ref as any}
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 1075 }) }}
          defaultValue={[]}
          isMulti={props.multi}
          value={finder ? null : selectedValue}
          options={data}
          className={size === "small" ? "basic-multi-select" : "custom-select-box"}
          classNamePrefix="react-select"
          isClearable={clearable}
          isLoading={loading}
          isDisabled={props.disabled}
          placeholder={loading ? "Loading data please wait..." : placeholder}
          onChange={onChange}
          closeMenuOnSelect={false}
          components={{
            MultiValue: DraggableMultiValue,
            MultiValueRemove: CustomMultiValueRemove,
          }}
        />
      </SortableContext>
    </DndContext>
  );
});

export default MultiFieldSelect;
